/**
 * TPL LIMITED EDITION - Pan & Zoom Lightbox
 * Inspired by Jon Way Studio (jonway.studio)
 * Pure vanilla JS, no dependencies, GPU-accelerated
 * Uses AbortController for clean listener lifecycle
 */

class TPLLightbox {
  constructor() {
    this.lightbox = null;
    this.image = null;
    this.trigger = null;
    this.scale = 1;
    this.minScale = 1;
    this.maxScale = 3;
    this.translateX = 0;
    this.translateY = 0;
    this.isDragging = false;
    this.dragStartX = 0;
    this.dragStartY = 0;
    this.lastTranslateX = 0;
    this.lastTranslateY = 0;
    this._abortController = null;

    this.init();
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    this.lightbox = document.querySelector('[data-lightbox]');
    this.trigger = document.querySelector('[data-lightbox-trigger]');

    if (!this.lightbox || !this.trigger) return;

    this.image = this.lightbox.querySelector('[data-zoom-image]');
    this.imageContainer = this.lightbox.querySelector('.lightbox-image-container');
    this.closeBtn = this.lightbox.querySelector('.lightbox-close');
    this.zoomInBtn = this.lightbox.querySelector('.zoom-in');
    this.zoomOutBtn = this.lightbox.querySelector('.zoom-out');
    this.zoomLevel = this.lightbox.querySelector('.zoom-level');
    this.overlay = this.lightbox.querySelector('.lightbox-overlay');

    this.attachEventListeners();
  }

  attachEventListeners() {
    // Clean up previous listeners
    if (this._abortController) this._abortController.abort();
    this._abortController = new AbortController();
    const signal = this._abortController.signal;

    // Open lightbox
    this.trigger.addEventListener('click', () => this.open(), { signal });

    // Close lightbox
    this.closeBtn.addEventListener('click', () => this.close(), { signal });
    this.overlay.addEventListener('click', () => this.close(), { signal });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !this.lightbox.hasAttribute('hidden')) {
        this.close();
      }
    }, { signal });

    // Zoom controls
    this.zoomInBtn.addEventListener('click', () => this.zoomIn(), { signal });
    this.zoomOutBtn.addEventListener('click', () => this.zoomOut(), { signal });

    // Mouse wheel zoom
    this.imageContainer.addEventListener('wheel', (e) => this.handleWheel(e), { passive: false, signal });

    // Pan (drag)
    this.imageContainer.addEventListener('mousedown', (e) => this.handleDragStart(e), { signal });
    document.addEventListener('mousemove', (e) => this.handleDragMove(e), { signal });
    document.addEventListener('mouseup', () => this.handleDragEnd(), { signal });

    // Touch support
    this.initialPinchDistance = 0;
    this.initialPinchScale = 1;

    this.imageContainer.addEventListener('touchstart', (e) => this.handleTouchStartUnified(e), { passive: false, signal });
    this.imageContainer.addEventListener('touchmove', (e) => this.handleTouchMoveUnified(e), { passive: false, signal });
    this.imageContainer.addEventListener('touchend', () => this.handleTouchEnd(), { passive: true, signal });
  }

  open() {
    this.lightbox.removeAttribute('hidden');
    this.lightbox.setAttribute('role', 'dialog');
    this.lightbox.setAttribute('aria-modal', 'true');
    this.lightbox.setAttribute('aria-label', 'Image lightbox');
    document.body.style.overflow = 'hidden';
    this.reset();

    requestAnimationFrame(() => {
      this.lightbox.style.opacity = '0';
      this.lightbox.style.transition = 'opacity 0.3s ease';
      requestAnimationFrame(() => {
        this.lightbox.style.opacity = '1';
      });
    });
  }

  close() {
    this.lightbox.style.opacity = '0';
    const onEnd = () => {
      this.lightbox.removeEventListener('transitionend', onEnd);
      this.lightbox.setAttribute('hidden', '');
      document.body.style.overflow = '';
      this.reset();
    };
    this.lightbox.addEventListener('transitionend', onEnd);
  }

  reset() {
    this.scale = 1;
    this.translateX = 0;
    this.translateY = 0;
    this.lastTranslateX = 0;
    this.lastTranslateY = 0;
    this.updateTransform();
    this.updateZoomLevel();
  }

  zoomIn() {
    this.setZoom(this.scale + 0.5);
  }

  zoomOut() {
    this.setZoom(this.scale - 0.5);
  }

  setZoom(newScale) {
    this.scale = Math.max(this.minScale, Math.min(this.maxScale, newScale));
    this.constrainTranslation();
    this.updateTransform();
    this.updateZoomLevel();
  }

  handleWheel(e) {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.2 : 0.2;
    this.setZoom(this.scale + delta);
  }

  handleDragStart(e) {
    if (this.scale <= 1) return;

    this.isDragging = true;
    this.imageContainer.classList.add('is-dragging');
    this.dragStartX = e.clientX;
    this.dragStartY = e.clientY;
    this.lastTranslateX = this.translateX;
    this.lastTranslateY = this.translateY;

    e.preventDefault();
  }

  handleDragMove(e) {
    if (!this.isDragging) return;

    const deltaX = e.clientX - this.dragStartX;
    const deltaY = e.clientY - this.dragStartY;

    this.translateX = this.lastTranslateX + deltaX;
    this.translateY = this.lastTranslateY + deltaY;

    this.constrainTranslation();
    this.updateTransform();
  }

  handleDragEnd() {
    this.isDragging = false;
    this.imageContainer.classList.remove('is-dragging');
    this.lastTranslateX = this.translateX;
    this.lastTranslateY = this.translateY;
  }

  handleTouchStartUnified(e) {
    if (e.touches.length === 1 && this.scale > 1) {
      this.isDragging = true;
      this.dragStartX = e.touches[0].clientX;
      this.dragStartY = e.touches[0].clientY;
      this.lastTranslateX = this.translateX;
      this.lastTranslateY = this.translateY;
    } else if (e.touches.length === 2) {
      this.isDragging = false;
      this.initialPinchDistance = this.getDistance(e.touches[0], e.touches[1]);
      this.initialPinchScale = this.scale;
    }
  }

  handleTouchMoveUnified(e) {
    if (e.touches.length === 1 && this.isDragging) {
      e.preventDefault();
      const deltaX = e.touches[0].clientX - this.dragStartX;
      const deltaY = e.touches[0].clientY - this.dragStartY;

      this.translateX = this.lastTranslateX + deltaX;
      this.translateY = this.lastTranslateY + deltaY;

      this.constrainTranslation();
      this.updateTransform();
    } else if (e.touches.length === 2) {
      e.preventDefault();
      const currentDistance = this.getDistance(e.touches[0], e.touches[1]);
      const scaleChange = currentDistance / this.initialPinchDistance;
      this.setZoom(this.initialPinchScale * scaleChange);
    }
  }

  handleTouchEnd() {
    this.isDragging = false;
    this.lastTranslateX = this.translateX;
    this.lastTranslateY = this.translateY;
  }

  constrainTranslation() {
    if (this.scale <= 1) {
      this.translateX = 0;
      this.translateY = 0;
      return;
    }

    const rect = this.image.getBoundingClientRect();
    const containerRect = this.imageContainer.getBoundingClientRect();

    const maxTranslateX = (rect.width * this.scale - containerRect.width) / 2;
    const maxTranslateY = (rect.height * this.scale - containerRect.height) / 2;

    this.translateX = Math.max(-maxTranslateX, Math.min(maxTranslateX, this.translateX));
    this.translateY = Math.max(-maxTranslateY, Math.min(maxTranslateY, this.translateY));
  }

  updateTransform() {
    this.image.style.transform = `
      translate3d(${this.translateX}px, ${this.translateY}px, 0)
      scale(${this.scale})
    `;
  }

  updateZoomLevel() {
    const percentage = Math.round(this.scale * 100);
    this.zoomLevel.textContent = `${percentage}%`;

    this.zoomOutBtn.disabled = this.scale <= this.minScale;
    this.zoomInBtn.disabled = this.scale >= this.maxScale;
  }

  getDistance(touch1, touch2) {
    const dx = touch2.clientX - touch1.clientX;
    const dy = touch2.clientY - touch1.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  destroy() {
    if (this._abortController) this._abortController.abort();
  }
}

// Initialize lightbox
new TPLLightbox();
